<template>
  <article class="image-card">
    <div class="image-card__preview">
      <img v-if="activeTab === 'image' && !previewFailed" :key="previewKey" :src="entry.image_url" :alt="getMaterialDisplayTitle(entry)" @error="handlePreviewError" />
      <video v-else-if="activeTab === 'video' && !previewFailed" :key="previewKey" :src="entry.image_url" controls playsinline preload="metadata" @error="handlePreviewError"></video>
      <div v-else-if="activeTab === 'audio' && !previewFailed" class="image-card__audio-preview">
        <span class="image-card__audio-pill">{{ getAudioMaterialCategory(entry) === 'bgm' ? 'BGM' : '旁白' }}</span>
        <strong>{{ getMaterialDisplayTitle(entry) }}</strong>
        <audio :key="previewKey" :src="entry.image_url" controls preload="metadata" @error="handlePreviewError"></audio>
      </div>
      <div v-else class="image-card__preview-placeholder">
        <strong>{{ activeTab === 'video' ? '视频暂不可播放' : activeTab === 'audio' ? '音频暂不可播放' : '图片暂不可预览' }}</strong>
        <p>当前素材链接不可访问。通常是 OSS 资源失效、权限关闭，或原始媒体已下线。</p>
      </div>
    </div>

    <div class="image-card__body">
      <div class="image-card__head">
        <div>
          <div class="image-card__title-block">
            <strong :title="getMaterialDisplayTitle(entry)">{{ getMaterialDisplayTitle(entry) }}</strong>
            <span class="image-card__project">{{ getMaterialProjectLabel(entry, linkedProjectId) }}</span>
          </div>
        </div>
        <div class="image-card__head-actions">
          <el-tag size="small">{{ getMaterialSourceLabel(entry.source) }}</el-tag>
          <el-button v-if="canDeleteMaterial(entry)" size="small" type="danger" plain :loading="deletingMaterialId === entry.id" @click="handleDeleteMaterial(entry)">{{ activeTab === 'video' ? '删除视频' : activeTab === 'audio' ? '删除音频' : '删除图片' }}</el-button>
        </div>
      </div>

      <p class="image-card__description">{{ getMaterialDisplayDescription(entry) }}</p>

      <div class="image-card__meta">
        <span>素材类型：{{ getMaterialTypeLabel(entry.material_type) }}</span>
        <span v-if="entry.duration">时长：{{ entry.duration }} 秒</span>
        <span>更新时间：{{ formatDate(entry.updated_at) }}</span>
      </div>

      <div class="image-card__footer">
        <span>{{ getMaterialFooterLabel(entry, linkedProjectId) }}</span>
        <div class="image-card__actions">
          <el-button
            v-if="linkedProjectId && isFinalAudioMode && linkedAudioRole"
            size="small"
            type="primary"
            plain
            @click="handleUseMaterialAsFinalAudio(entry)"
          >
            设为当前{{ linkedAudioRole === 'narration' ? '旁白' : 'BGM' }}素材
          </el-button>
          <el-button
            v-if="linkedProjectId && isConsistencyAssetImageMode"
            size="small"
            type="primary"
            plain
            @click="handleUseMaterialAsConsistencyAssetImage(entry)"
          >
            设为当前{{ linkedAssetTypeLabel }}参考图
          </el-button>
          <el-button v-else-if="activeTab === 'image' && linkedProjectId && linkedSceneNumber" size="small" type="primary" plain @click="handleUseMaterialAsSceneImage(entry)">设为当前分镜图片</el-button>
          <el-button v-else-if="linkedProjectId" size="small" plain @click="linkMaterialToWorkflow(entry)">加入当前项目</el-button>
          <ReferenceLinkActions :url="entry.image_url" compact :show-status="false" />
        </div>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import ReferenceLinkActions from '@/components/ReferenceLinkActions.vue'
import { getAudioMaterialCategory } from './audioMaterialLibrary'
import {
  getMaterialDisplayDescription,
  getMaterialDisplayTitle,
  getMaterialFooterLabel,
  getMaterialProjectLabel,
  getMaterialTypeLabel,
} from './materialLibrary'
import type { MaterialLibraryEntry } from './types'

const props = defineProps<{
  activeTab: 'image' | 'video' | 'audio'
  entry: MaterialLibraryEntry
  previewFailed: boolean
  linkedProjectId: number | null
  linkedSceneNumber: number | null
  linkedAssetTypeLabel: string
  linkedAudioRole: 'narration' | 'bgm' | null
  isConsistencyAssetImageMode: boolean
  isFinalAudioMode: boolean
  deletingMaterialId: number | null
  formatDate: (value?: string | null) => string
  getMaterialSourceLabel: (source: string) => string
  canDeleteMaterial: (entry: MaterialLibraryEntry) => boolean
  handleDeleteMaterial: (entry: MaterialLibraryEntry) => void | Promise<void>
  handleUseMaterialAsFinalAudio: (entry: MaterialLibraryEntry) => void
  handleUseMaterialAsConsistencyAssetImage: (entry: MaterialLibraryEntry) => void
  handleUseMaterialAsSceneImage: (entry: MaterialLibraryEntry) => void
  linkMaterialToWorkflow: (entry: MaterialLibraryEntry) => void
}>()

const emit = defineEmits<{
  (event: 'media-error', id: number): void
}>()

const previewRetryCount = ref(0)
const previewKey = computed(() => `${props.activeTab}:${props.entry.id}:${props.entry.image_url || ''}:${previewRetryCount.value}`)

watch(
  () => `${props.activeTab}:${props.entry.image_url || ''}`,
  () => {
    previewRetryCount.value = 0
  }
)

function handlePreviewError() {
  if (previewRetryCount.value < 1) {
    previewRetryCount.value += 1
    return
  }

  emit('media-error', props.entry.id)
}
</script>

<style scoped>
.image-card__head,
.image-card__footer,
.image-card__head-actions,
.image-card__actions {
  display: flex;
  gap: 16px;
}

.image-card {
  padding: 22px;
  border-radius: 24px;
  background: rgba(248, 250, 252, 0.86);
  border: 1px solid rgba(148, 163, 184, 0.18);
  display: grid;
  grid-template-columns: minmax(200px, 240px) minmax(0, 1fr);
  gap: 18px;
  align-items: start;
}

.image-card__preview {
  overflow: hidden;
  border-radius: 18px;
  aspect-ratio: 1 / 1;
  min-height: 0;
  max-height: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #eef2f7 0%, #dde5ef 100%);
}

.image-card__preview img,
.image-card__preview video {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.image-card__audio-preview {
  width: 100%;
  height: 100%;
  padding: 20px;
  display: grid;
  align-content: center;
  gap: 12px;
  background: linear-gradient(160deg, rgba(15, 23, 42, 0.92) 0%, rgba(30, 41, 59, 0.88) 100%);
  color: #f8fafc;
}

.image-card__audio-preview strong {
  font-size: 16px;
  line-height: 1.5;
}

.image-card__audio-preview audio {
  width: 100%;
}

.image-card__audio-pill {
  display: inline-flex;
  width: fit-content;
  padding: 5px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  font-size: 12px;
  letter-spacing: 0.08em;
}

.image-card__preview img {
  object-position: center center;
}

.image-card__preview-placeholder {
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  padding: 18px;
  text-align: center;
  color: #475569;
}

.image-card__preview-placeholder strong {
  color: #0f172a;
  font-size: 15px;
}

.image-card__preview-placeholder p {
  margin: 8px 0 0;
  line-height: 1.6;
  color: #64748b;
}

.image-card__body {
  display: grid;
  gap: 14px;
  min-width: 0;
}

.image-card__head,
.image-card__footer {
  justify-content: space-between;
  align-items: flex-start;
}

.image-card__title-block {
  min-width: 0;
}

.image-card__head-actions,
.image-card__actions {
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.image-card__head-actions {
  justify-content: flex-end;
  gap: 8px;
}

.image-card__head strong {
  display: block;
  color: #0f172a;
  font-size: 16px;
  line-height: 1.4;
  overflow: hidden;
  word-break: break-word;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
}

.image-card__project,
.image-card__description {
  overflow-wrap: anywhere;
  word-break: break-word;
}

.image-card__project,
.image-card__head span,
.image-card__footer span {
  color: #64748b;
  font-size: 13px;
}

.image-card__project {
  display: inline-flex;
  margin-top: 6px;
}

.image-card__description {
  margin: 0;
  color: #475569;
  line-height: 1.8;
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.image-card__meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.image-card__meta span {
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  color: #64748b;
  font-size: 12px;
}

@media (max-width: 1080px) {
  .image-card {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .image-card__head,
  .image-card__footer,
  .image-card__actions {
    flex-direction: column;
    align-items: stretch;
  }

  .image-card__meta {
    grid-template-columns: 1fr;
  }
}
</style>